import React from "react";
import { connect, Field, FieldArray } from "formik";
import { InputField, SimpleCheckboxGroup } from "@coverme/forms";
import { get } from "lodash-es";
import { SimpleSingleSelect } from "../../../components/FormComponents/SimpleSingleSelect";
import { AddAdditional, CurrencyInputField } from "@coverme/forms";
import deleteIconSrc from "../../../resources/delete-icon.svg";
import { YesNoButtons } from "../../../components/FormComponents/YesNoButtons";

const EmploymentStatus = ({ fieldName, personField, setFieldValue, personName, getValidationState, getError }) => (
    <Field
        name={fieldName}
        render={({ field }) => (
            <SimpleCheckboxGroup
                title={`What is ${personName}'s employment status?`}
                flexDirection={"row"}
                changeEvent={(e, i) => {
                    let valueToSet = [...i];
                    // If None is the first item and another item has been selected, unselect None
                    if (i.indexOf("Unemployed") === 0 && i.length > 1) {
                        valueToSet = i.filter(x => x !== "Unemployed");
                    }

                    // If None has been selected after something else, remove other options.
                    if (i.indexOf("Unemployed") > 0) {
                        valueToSet = ["Unemployed"];
                    }
                    setFieldValue(field.name, valueToSet);
                }}
                required={true}
                selectedValues={field.value}
                groupName={`${personField}.employment-status`}
                validState={getValidationState(field)}
                errorMessage={getError(field)}
                items={["Employed", "Self-employed", "Unemployed"]}
            />
        )}
    />
);

const EmployerComponent = ({
    fieldBaseName,
    getErrors,
    getValidationState,
    isRequired,
    setFieldTouched,
    setFieldValue,
    incomeFrequency,
    values,
    index,
    deleteCard,
}) => {
    let salaryWagesMessage = "";
    const frequency = get(values, `${fieldBaseName}.frequency`) || "";
    if (frequency === "Every 2 weeks" || frequency === "Twice a month" || frequency === "Twice a year") {
        salaryWagesMessage = `What is the wage paid ${frequency?.toLowerCase()}?`;
    } else if (frequency === "Annually") {
        salaryWagesMessage = `What is the annual wage?`;
    } else {
        salaryWagesMessage = `What is the ${frequency?.toLowerCase()} wage?`;
    }
    return (
        <fieldset className="interview-subsection">
            <legend>Current Job {index + 1}</legend>
            <div className="form-section">
                <div className="form-control">
                    <Field
                        name={`${fieldBaseName}.employerName`}
                        render={({ field }) => (
                            <InputField
                                id={field.name}
                                title={"Employer Name"}
                                placeholder={"Employer Name"}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                {...field}
                            />
                        )}
                    />
                </div>
            </div>
            <div className="form-section">
                <div className="form-control m-input">
                    <Field
                        name={`${fieldBaseName}.frequency`}
                        render={({ field }) => (
                            <SimpleSingleSelect
                                {...field}
                                title={"Income Frequency"}
                                id={field.name}
                                setFieldTouched={setFieldTouched}
                                setFieldValue={setFieldValue}
                                required={isRequired(field)}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                options={incomeFrequency}
                            />
                        )}
                    />
                </div>
                {get(values, `${fieldBaseName}.frequency`) === "Hourly" && (
                    <div className="form-control m-input">
                        <Field
                            name={`${fieldBaseName}.averageHoursWorkedPerWeek`}
                            render={({ field }) => (
                                <InputField
                                    {...field}
                                    id={field.name}
                                    placeholder={"Hours Worked per Week"}
                                    title={"Hours Worked per Week"}
                                    errorMessage={getErrors(field)}
                                    required={isRequired(field)}
                                    validState={getValidationState(field)}
                                    type="tel"
                                    min={0}
                                />
                            )}
                        />
                    </div>
                )}
                <div className="form-control m-250-input">
                    <Field
                        name={`${fieldBaseName}.amount`}
                        render={({ field }) => (
                            <CurrencyInputField
                                {...field}
                                id={field.name}
                                value={field.value}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                title={salaryWagesMessage}
                                changeEvent={e => {
                                    setFieldValue(field.name, e);
                                }}
                            />
                        )}
                    />
                </div>
            </div>
            {index > 0 && (
                <div className="delete-icon" onClick={() => deleteCard()}>
                    <img src={deleteIconSrc} />
                </div>
            )}
        </fieldset>
    );
};

const SelfEmploymentComponent = ({ personField, getErrors, isRequired, getValidationState, setFieldValue }) => {
    return (
        <fieldset className="interview-subsection">
            <legend>Self Employment</legend>
            <div className="form-section">
                <div className="form-control auto-input">
                    <Field
                        name={`${personField}.income.selfEmployment.typeOfWork`}
                        render={({ field }) => (
                            <InputField
                                {...field}
                                id={field.name}
                                placeholder={"Type of work"}
                                title={"Type of work"}
                                errorMessage={getErrors(field)}
                                required={isRequired(field)}
                                validState={getValidationState(field)}
                                min={0}
                            />
                        )}
                    />
                </div>
                <div className="form-control auto-input">
                    <Field
                        name={`${personField}.income.selfEmployment.amount`}
                        render={({ field }) => (
                            <CurrencyInputField
                                {...field}
                                id={field.name}
                                value={field.value}
                                errorMessage={getErrors(field)}
                                validState={getValidationState(field)}
                                required={isRequired(field)}
                                title={"Net income for this month"}
                                changeEvent={e => {
                                    setFieldValue(field.name, e);
                                }}
                            />
                        )}
                    />
                </div>
            </div>
        </fieldset>
    );
};

const OtherIncomesComponent = ({
    personField,
    getErrors,
    isRequired,
    getValidationState,
    setFieldValue,
    incomeFrequency,
    incomeTypes,
    setFieldTouched,
    personName,
    values,
}) => {
    const otherIncomeTypes = incomeTypes.filter(
        x => x !== "Salaries/Wages" && x !== "Self Employed" && x !== "Net farming/fishing"
    );
    const usefulFrequencies = incomeFrequency.filter(x => x !== "Hourly");
    return (
        <>
            <div className="form-section">
                <div className="form-control xxl-input">
                    <YesNoButtons
                        name={`${personField}.income.hasUnearnedIncome`}
                        title={`Does ${personName} have other sources of income?`}
                        setFieldValue={setFieldValue}
                        getErrors={getErrors}
                        isRequired={isRequired}
                        getValidationState={getValidationState}
                    />
                </div>
            </div>
            {get(values, `${personField}.income.hasUnearnedIncome`) == true && (
                <fieldset className="interview-subsection ">
                    <legend>Other income types</legend>

                    <div className="other-income-table">
                        <div className="table-row table-header">
                            <div className="col col-1">Income Type</div>
                            <div className="col col-1">Frequency</div>
                            <div className="col col-1">Amount</div>
                        </div>
                        {otherIncomeTypes.map((x, j) => {
                            return (
                                <div key={j} className="table-row">
                                    <div className="col col-1 col-header">{x}</div>
                                    {/* this inline css was necessary to correct the z-index issue in ie */}
                                    <div
                                        className="col col-1"
                                        style={{ zIndex: `${11 - otherIncomeTypes.indexOf(x)}` }}
                                    >
                                        <Field
                                            name={`${personField}.income.unearned.${x.toLowerCase()}.frequency`}
                                            render={({ field }) => (
                                                <SimpleSingleSelect
                                                    {...field}
                                                    id={field.name}
                                                    value={field.value || "Monthly"}
                                                    setFieldTouched={setFieldTouched}
                                                    setFieldValue={setFieldValue}
                                                    required={isRequired(field)}
                                                    errorMessage={getErrors(field)}
                                                    validState={getValidationState(field)}
                                                    options={usefulFrequencies}
                                                />
                                            )}
                                        />
                                    </div>
                                    <div className="col col-1">
                                        <Field
                                            name={`${personField}.income.unearned.${x.toLowerCase()}.amount`}
                                            render={({ field }) => (
                                                <CurrencyInputField
                                                    {...field}
                                                    id={field.name}
                                                    value={field.value}
                                                    placeholder={"Amount"}
                                                    errorMessage={getErrors(field)}
                                                    required={isRequired(field)}
                                                    validState={getValidationState(field)}
                                                    min={0}
                                                    changeEvent={e => {
                                                        setFieldValue(field.name, e);
                                                    }}
                                                />
                                            )}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </fieldset>
            )}
        </>
    );
};

const IncomeComponent = ({
    personField,
    getValidationState,
    getErrors,
    isRequired,
    incomeFrequency,
    incomeTypes,
    personName,
    formik: { setFieldValue, setFieldTouched, values, ...props },
}) => {
    const employmentStatusFieldName = `${personField}.income.employmentStatus`;
    const selectedIncomeTypes = get(values, employmentStatusFieldName);
    return (
        <>
            <div className="form-section">
                <div className="form-control employment-status">
                    <EmploymentStatus
                        setFieldValue={setFieldValue}
                        personField={personField}
                        fieldName={employmentStatusFieldName}
                        personName={personName}
                        getError={getErrors}
                        getValidationState={getValidationState}
                    />
                </div>
            </div>
            {selectedIncomeTypes.includes("Employed") && (
                <FieldArray
                    name={`${personField}.income.employers`}
                    render={arrayHelpers => {
                        const employers = get(values, `${personField}.income.employers`);
                        const employerComponents = employers.map((x, i) => {
                            const fieldBaseName = `${personField}.income.employers.${i}`;
                            return (
                                <div style={{ zIndex: employers.length - i }}>
                                    <EmployerComponent
                                        index={i}
                                        key={`${personField}.income.employers.${i}`}
                                        fieldBaseName={fieldBaseName}
                                        getValidationState={getValidationState}
                                        getErrors={getErrors}
                                        values={values}
                                        isRequired={isRequired}
                                        setFieldValue={setFieldValue}
                                        setFieldTouched={setFieldTouched}
                                        incomeFrequency={incomeFrequency}
                                        deleteCard={() => arrayHelpers.remove(i)}
                                    />
                                </div>
                            );
                        });

                        return (
                            <>
                                {employerComponents}
                                <div id={`employer_add_additional`} style={{ width: "fit-content" }}>
                                    <AddAdditional
                                        labelText="Add another employer"
                                        required={isRequired}
                                        clickEvent={() => {
                                            arrayHelpers.push({
                                                employerName: "",
                                                frequency: "",
                                                averageHoursWorkedPerWeek: null,
                                                amount: null,
                                            });
                                        }}
                                    />
                                </div>
                            </>
                        );
                    }}
                />
            )}
            {selectedIncomeTypes.includes("Self-employed") && (
                <SelfEmploymentComponent
                    setFieldValue={setFieldValue}
                    getErrors={getErrors}
                    getValidationState={getValidationState}
                    personField={personField}
                    isRequired={isRequired}
                />
            )}
            {selectedIncomeTypes.length > 0 && (
                <OtherIncomesComponent
                    setFieldValue={setFieldValue}
                    getErrors={getErrors}
                    getValidationState={getValidationState}
                    personField={personField}
                    isRequired={isRequired}
                    incomeFrequency={incomeFrequency}
                    incomeTypes={incomeTypes}
                    personName={personName}
                    values={values}
                    setFieldTouched={setFieldTouched}
                />
            )}
        </>
    );
};

export const IncomeFields = connect(IncomeComponent);

export default IncomeFields;
